import { lazy } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { contentRoutes, footerLinks } from '../config';
import { BlogHomepage } from './components/Blog/BlogHomepage';
import { BlogPost } from './components/Blog/BlogPost';
import Home from './pages/Home/container';

// Lazy load everything except home, we want to optimize LCP
const Booking = lazy(() => import('./pages/Booking/Booking'));
const FetchContent = lazy(() => import('./pages/Content/FetchContent'));
const TermsConditions = lazy(() => import('./pages/Content/TermsConditions'));
const TravelGuide = lazy(() => import('./pages/Content/TravelGuide'));
const Parks = lazy(() => import('./pages/Browse/Parks'));
const OperatorRegions = lazy(() => import('./pages/Browse/OperatorRegions'));
const Regions = lazy(() => import('./pages/Browse/Regions'));
const TravelGuideHome = lazy(() => import('./pages/TravelGuideHome/TravelGuideHome'));
const Reset = lazy(() => import('./pages/Reset'));
const AllDatesSearchResults = lazy(() => import('./pages/SearchResults/AllDatesSearchResults'));
const AllRegionsSearchResults = lazy(() => import('./pages/SearchResults/AllRegionsSearchResult'));
const Help = lazy(() => import('./pages/Help/Help'));
const MyBooking = lazy(() => import('./pages/Booking/MyBooking/MyBooking'));
const NotFound = lazy(() => import('./pages/404'));
const SearchResults = lazy(() => import('./pages/SearchResults/SearchResults'));
const Login = lazy(() => import('./pages/Booking/Login/Login'));
const ParkPage = lazy(() =>
  import('./pages/HolidayPark/ParkPage').then((module) => {
    return { default: module.ParkPage };
  }),
);

const footerData = Object.values(footerLinks)
  .flatMap((item) => item.links)
  .filter((item) => 'pageId' in item);

const fetchContentRoutes = [...contentRoutes, ...footerData];

// Ensure that /a/1234/path links still work
const legacyToNewRoutes = footerData.map(({ pageId, path }) => ({
  from: `/a/${pageId}${path}`,
  to: path,
}));

const parkPaths = ['boating-holidays', 'holidays', 'boat', 'boat-result', 'park-result'];

export const Routes = () => (
  <Switch>
    {/* Fixed Routes */}
    <Route path="/" exact component={Home} />
    <Route path="/login" component={Login} />
    <Route path="/my-booking" component={MyBooking} />
    <Route path="/reset" component={Reset} />
    <Route path="/404" component={NotFound} />

    {/* Blog */}
    <Route path="/blog" exact component={BlogHomepage} />
    <Route path="/blog/:blogPost" component={BlogPost} />

    {/* Search & Booking */}
    <Route path="/results/all-regions" component={AllRegionsSearchResults} />
    <Route path="/results/all-dates" component={AllDatesSearchResults} />
    <Route path="/results" component={SearchResults} />
    <Route path="/booking" component={Booking} exact />
    <Route path="/booking/:step" component={Booking} />

    {/* Browsing / Regions */}
    <Route path="/channel/:channelCode" strict component={Regions} />
    <Route path="/region/:channelCode" strict component={Regions} />
    <Redirect exact from="/dog-friendly/uk" to="/dog-friendly/ukcaravan" />
    <Route path="/dog-friendly/:channelCode" strict component={Regions} />
    <Route path="/:operatorId/region/:channelCode" strict component={OperatorRegions} />

    {/* Parks */}
    <Route path="/parks/:channelId([a-zA-Z]+)/:regionId/:slug?" component={Parks} />
    <Route path="/holidays/:channelId([a-zA-Z]+)/:regionId/:slug?" component={Parks} exact />
    <Route path="/boating-holidays/:channelId([a-zA-Z]+)/:regionId/:slug?" component={Parks} />
    <Route path="/area/:channelId([a-zA-Z]+)/:regionId/:townId/:slug?" component={Parks} />

    {/* Legacy / Park routes */}
    {/* Convert the old park routes to the new format, by removing channelId and regionId */}
    <Route
      exact
      path="/:path/:channelId/:regionId/:parkId/:slug?"
      render={({ match }) => {
        const { path, parkId, slug } = match.params;
        const toPath = slug ? `/${path}/${parkId}/${slug}` : `/${path}/${parkId}`;
        return <Redirect to={toPath} />;
      }}
    />
    {parkPaths.map((path) => (
      <Route key={`${path}-target`} exact path={`/${path}/:parkId/:slug?`} component={ParkPage} />
    ))}

    {/* Content / CMS */}
    <Route path="/inspiration" component={TravelGuideHome} />
    <Route path="/terms" component={TermsConditions} />
    <Route path="/help" component={Help} />
    <Redirect exact from="/contact-us" to="/help" />
    <Route path="/travel-guide/:regionName/:regionId" component={TravelGuide} />
    {fetchContentRoutes.map(({ path, pageId }) => (
      <Route key={path} path={path} component={() => <FetchContent pageId={pageId} />} />
    ))}
    {legacyToNewRoutes.map(({ from, to }) => (
      <Redirect key={from} exact from={from} to={to} />
    ))}
    <Route path="/p/age/965" component={() => <FetchContent pageId={965} />} />

    <Route path="/a/:contentId/:keywords">
      <FetchContent />
    </Route>
    
    {/* Redirects */}
    <Redirect from="/rentals" to="/region/ukcottages" />
    <Route path="/my-account">
      <Redirect to="/login" />
    </Route>

    {/* Catch-all NotFound */}
    <Route component={NotFound} />
  </Switch>
);
